import { useEffect, useMemo } from 'react';
import { getCssVars } from '../utils/getCssVars';
import { injectPromo } from '../utils/injectPromo';
import { useWindowSize } from 'react-use';

interface PromoTileMedia {
  media?: {
    file?: string;
  };
}

interface PromoTileItem {
  promoBackgroundColor?: string;
  cellSpan: number;
  gridPosition: number;
  promoText?: string;
  promoBackgroundImage?: PromoTileMedia;
  promoBackgroundVideo?: string;
  promoTitle?: string;
  promoLink?: string;
  promoConditions?: string;
  promoTitleFont?: string;
  promoTitleLineHeight?: string;
  promoTextFont?: string;
  promoTextLineHeight?: string;
  promoConditionsFont?: string;
  promoConditionsLineHeight?: string;
  showOnMobile?: boolean;
  showOnTablet?: boolean;
  showOnDesktop?: boolean;
}

interface PromoData {
  promoTile?: PromoTileItem[];
}

interface StreamData {
  items: any[];
}

interface UsePromoProps {
  data: PromoData;
  hasPromoTile: boolean;
  stream: StreamData;
  itemOffset: number;
  isGlobalLoaderShow: boolean;
  hidePromos: boolean;
  isSSR?: boolean;
}

export const usePromo = ({ data, hasPromoTile, stream, itemOffset, isGlobalLoaderShow, hidePromos, isSSR}: UsePromoProps) => {
  if (!data?.promoTile?.length) {
    return {
      promoData: null,
    };
  }

  const { width } = useWindowSize();
  const isMobile = width < 640;
  const isTablet = width >= 641 && width <= 1186;
  const isDesktop = !isTablet && !isMobile;

  const filteredPromos = (promoTiles: PromoTileItem[]) => {
    if (isMobile) {
      return promoTiles.filter((promo) => promo.showOnMobile);
    }
    if (isTablet) {
      return promoTiles.filter((promo) => promo.showOnTablet);
    }
    if (isDesktop) {
      return promoTiles.filter((promo) => promo.showOnDesktop);
    }
    return [];
  };

  const promoData = useMemo(() => {
    return filteredPromos(data?.promoTile)
      ?.sort((a, b) => a.gridPosition - b.gridPosition)
      .map((item) => {
        const {
          promoBackgroundColor,
          cellSpan,
          gridPosition,
          promoText,
          promoBackgroundImage,
          promoBackgroundVideo,
          promoTitle,
          promoLink,
          promoConditions,
        } = item;
        const layoutMaxCount = 4;

        return {
          backgroundColor: promoBackgroundColor,
          text: promoText,
          smallText: promoConditions,
          image: promoBackgroundImage?.media?.file,
          video: promoBackgroundVideo,
          title: promoTitle,
          layout: layoutMaxCount,
          cellSpan,
          gridPosition,
          promoLink,
          textStyles: {
            ...getCssVars(item.promoTitleFont, 'promoTitleFontSize'),
            ...getCssVars(item.promoTitleLineHeight, 'promoTitleLineHeight'),
            ...getCssVars(item.promoTextFont, 'promoTextFontSize'),
            ...getCssVars(item.promoTextLineHeight, 'promoTextLineHeight'),
            ...getCssVars(item.promoConditionsFont, 'promoConditionsFontSize'),
            ...getCssVars(item.promoConditionsLineHeight, 'promoConditionsLineHeight'),
          },
        };
      });
  }, [data, isMobile, isTablet, isDesktop]);

  useEffect(() => {
    if (hidePromos) return;
    if (hasPromoTile && !isGlobalLoaderShow) {
      injectPromo(promoData, stream.items, itemOffset);
    }
  }, [itemOffset, isTablet, isMobile, isGlobalLoaderShow, hidePromos, isSSR, data]);

  return { promoData };
};
